import { isString } from 'lodash-es';
import moment from 'moment-timezone';

import { MARKET_DATA_TIME_ZONE } from '../constants/date-time.constants';
import {
  CallStatus, OrderSideEnum, OrderStatusEnum, TimeInForceEnum,
} from '../enums';
import { OrderTypeEnum } from '../enums/order-type.enum';
import lang from '../language';
import { Order, Position } from '../models/trading/types';
import { SubscribeGroup } from '../store/market-data/types';
import { ModifyOrCancelOrderRequest } from '../store/trading/types';
import { BBOData, TradeData } from '../models/market-data/types';
import { SOROrderSide, SOROrderRequestDataBase, SOROrderType } from '../models/gateway/types';
import { formatCurrency } from './DataHelpers';
import { getProp, getPropName } from './ObjectTools';
import { TextHelpers } from './TextHelpers';
import { mapOrderStatus } from './trading-status-helpers';
import {
  INVALID_VALUE, NullableNumber, NullableString, OrderTicketComponent, TypedObject,
} from './types';

const VALID_ORDER_SIDES: string[] = [
  OrderSideEnum.Buy,
  OrderSideEnum.Sell,
] as string[];

export function mapOrderTimeInForce(value: NullableString): string {
  switch (value) {
    case 'DAY':
      return lang.commonOrderTimeInForceDay();

    case 'ON_OPEN':
      return lang.commonOrderTimeInForceOnOpen();

    case 'IOC':
      return lang.commonOrderTimeInForceIOC();

    case 'EXTENDED_DAY':
      return lang.commonOrderTimeInForceExtDay();

    case 'ON_CLOSE':
      return lang.commonOrderTimeInForceOnClose();

    case 'GTC':
      return 'GTC';

    default:
      if (value !== null && value !== undefined && value.length > 0) {
        return value;
      }
      return INVALID_VALUE;
  }
}

export function calculatePositionExposure(position: Position, currentPrice: number): number {
  /**
   * Get the absolute value of the parsed quantity
   * For short positions qty is negative
   */
  return Math.abs(position.qty) * currentPrice;
}

export function calculatePositionUnrealizedPnL(position: Position, currentPrice: number): number {
  const positionQty = position.qty;
  const absolutePositionQty = Math.abs(positionQty);

  /**
   * positionQty more than zero indicates that the position is 'Long'
   */
  if (positionQty > 0) {
    /**
     * Unrealized PnL for 'Long' positions
     *
     * Get absolute value of positionQty(if it -1, -25 ...etc. 1, 25 will be returned)
     */
    return (currentPrice * absolutePositionQty) - (position.avgPrice * absolutePositionQty);
  }

  /**
   * positionQty less than zero indicates that the position is 'Short'
   *
   * Unrealized PnL for 'Short' positions
   */
  return (position.avgPrice * absolutePositionQty) - (currentPrice * absolutePositionQty);
}

export function calculatePositionUnrealizedPnLInPercentage(position: Position, currentPrice: number): number {
  const positionQty = position.qty;
  const absolutePositionQty = Math.abs(positionQty);
  const unrealizedPnL = calculatePositionUnrealizedPnL(position, currentPrice);

  /**
   * positionQty more than zero indicates that the position is 'Long'
   */
  if (positionQty > 0) {
    /**
     * Unrealized PnL for 'Long' positions
     *
     * Get absolute value of positionQty(if it -1, -25 ...etc. 1, 25 will be returned)
     */
    return (unrealizedPnL / (position.avgPrice * absolutePositionQty)) * 100;
  }

  /**
   * positionQty less than zero indicates that the position is 'Short'
   *
   * Unrealized PnL for 'Short' positions
   */
  return (unrealizedPnL / (currentPrice * absolutePositionQty)) * 100;
}

export function getMarketDataTopic(response: any): string | null {
  return (response.topic
    || ((getProp(response, '0') === 'heartbeat') && 'heartbeat')
    || getPropName(response, 'query')
    || getPropName(response, 'subscribe')
    || getPropName(response, 'login')
  );
}

export function calculateSymbolNBBOAndTradePrices(
  symbol: NullableString,
  side: NullableString,
  nbboPrices: TypedObject<BBOData>,
  tradePrices: TypedObject<TradeData[]>,
) {
  if (symbol == null || !isString(symbol)) {
    throw new Error(`[TradingHelpers] calculateSymbolPrices - invalid value for symbol - '${symbol}'`);
  }
  // if ((OrderSideEnum as any)[side!] == null) {
  if (!VALID_ORDER_SIDES.includes(side!)) {
    throw new Error(`[TradingHelpers] calculateSymbolPrices - invalid value for side - '${side}'`);
  }

  const nbboAskPrice: NullableNumber = nbboPrices[symbol!]?.askprice;
  const nbboBidPrice: NullableNumber = nbboPrices[symbol!]?.bidprice;
  const trades = tradePrices[symbol!] ?? [];
  const tradePrice: NullableNumber = trades[trades.length - 1].tradeprice;

  let symbolPrice;
  if (side === OrderSideEnum.Buy) {
    symbolPrice = nbboAskPrice;
  } else {
    symbolPrice = nbboBidPrice;
  }

  return {
    symbolPrice, nbboAskPrice, nbboBidPrice, tradePrice,
  };
}

export function orderStatusToCallStatus(orderStatus?: OrderStatusEnum | null) {
  let callStatus: CallStatus;

  switch (orderStatus) {
    case OrderStatusEnum.Accepted:
    case OrderStatusEnum.Queued:
    case OrderStatusEnum.PartiallyFilled:
    case OrderStatusEnum.Filled:
    case OrderStatusEnum.Cancelled:
    case OrderStatusEnum.Modified:
      callStatus = CallStatus.READY;
      break;

    case OrderStatusEnum.Rejected:
    case OrderStatusEnum.TimedOut:
      callStatus = CallStatus.ERROR;
      break;

    default:
      callStatus = CallStatus.INITIAL;
      break;
  }

  return callStatus;
}

export const mapOrderSORtoSide = (sorSide: SOROrderSide) => {
  switch (sorSide) {
    case SOROrderSide.Buy:
      return OrderSideEnum.Buy;
    case SOROrderSide.Sell:
      return OrderSideEnum.Sell;
    case SOROrderSide.SellShort:
      return OrderSideEnum.SellShort;

    default:
      console.warn(`[TradingHelpers] mapOrderSide - unknown input value - ${sorSide} - using 'Buy' by default`);
      return OrderSideEnum.Buy;
  }
};

export function setSOROrderData(data: Order | ModifyOrCancelOrderRequest) {
  const {
    destination,
    symbol,
    clientOrderId,
    orderQty,
    ordType,
    side = OrderSideEnum.Buy,
    stopPrice,
    price,
    timeInForce,
    includeExtendedHours,
    traceIdentifier,
  } = data as Order;
  let { order, account } = data as ModifyOrCancelOrderRequest;
  if (!order) account = (data as ModifyOrCancelOrderRequest).account;
  const orderData: SOROrderRequestDataBase = {
    clOrdId: clientOrderId!,
    orderQty: orderQty!,
    exDestination: destination ?? 'DEMO',
    account,
    side,
    symbol: symbol!,
    timeInForce: timeInForce as TimeInForceEnum,
    traceIdentifier,
    tradingSessionId: includeExtendedHours ? 'X' : 'M',
  };

  switch (ordType) {
    case OrderTypeEnum.Market:
      orderData.type = 'MarketOrder';
      break;
    case OrderTypeEnum.Limit:
      orderData.type = 'LimitOrder';
      orderData.price = price;
      break;
    case OrderTypeEnum.Stop:
      orderData.type = 'StopOrder';
      orderData.stopPx = stopPrice;
      break;
    default:
      console.error(`[Global.service] Error setting order type -- ${ordType}`);
      break;
  }

  return orderData;
}

export const parseOrderTicketComponentToSubscribeGroup = (component: OrderTicketComponent): SubscribeGroup | null => {
  let group: SubscribeGroup | null = null;
  switch (component) {
    case 'initial':
      group = component;
      break;

    case 'watchlist/popular':
    case 'watchlist/favorites':
    case 'watchlist':
      group = 'watchlist';
      break;

    case 'discover':
    case 'discover/epic-movers':
    case 'discover/week52-gainers-losers':
    case 'discover/hot-gappers':
    case 'discover/top-movers':
    case 'discover/popular-group':
      group = 'discover';
      break;

    case 'discover/new-on-the-market':
      group = 'discover';
      break;

    case 'my-account':
    case 'my-account/positions':
    case 'my-account/orders':
    case 'my-account/account-breakdown':
      group = 'my-account';
      break;

    case 'history': {
      group = component;
      break;
    }

    case 'people-also-trade':
      group = component;
      break;

    case 'symbol-details': {
      group = 'symbol-details';
      break;
    }

    case 'chart-details': {
      group = 'chart-details';
      break;
    }

    case 'create-order': {
      group = 'create-order';
      break;
    }

    case 'modify-order': {
      group = 'modify-order';
      break;
    }

    case 'funds': {
      group = null;
      break;
    }

    case 'profile': {
      group = null;
      break;
    }

    default:
      break;
  }

  return group;
};

export function parseOrderProp(value: any, name: string) {
  if (value == null) return INVALID_VALUE;

  let result = value;

  switch (name) {
    case 'updatedAt':
    case 'createdAt':
      // Reading data as UTC and showing it in EST (New York time)
      // result = moment(value).utc().format('YYYY-MM-DD HH:mm:s.SSS [UTC]');
      result = moment.tz(value, MARKET_DATA_TIME_ZONE).format('YYYY-MM-DD HH:mm:ss z');
      break;

    case 'price':
      result = formatCurrency(value);
      break;

    case 'status':
      result = mapOrderStatus(value);
      break;

    case 'timeInForce':
      result = mapOrderTimeInForce(value);
      break;

    default:
      console.warn(`[DataHelpers] parseOrderProp - unknown prop name '${name}' with value '${value}'`);
      break;
  }

  if (result == null) {
    result = INVALID_VALUE;
  }


  return result;
}

export const getOrderStatusLabelByName = (orderStatus: NullableString): string => {
  switch (orderStatus) {
    case OrderStatusEnum.Filled:
      return lang.commonOrderStatusFilterFilled();
    case OrderStatusEnum.Cancelled:
      return lang.commonOrderStatusCanceled();
    case OrderStatusEnum.Rejected:
      return lang.commonOrderStatusRejected();
    case OrderStatusEnum.PartiallyFilled:
      return lang.commonOrderStatusFilterPartiallyFilled();
    case OrderStatusEnum.PartiallyReject:
      return lang.commonOrderStatusFilterPartiallyRejected();
    case OrderStatusEnum.Expired:
    case OrderStatusEnum.OtherwiseExpired:
      return lang.commonOrderStatusFilterExpired();
    default:
      return '';
  }
};

export function getExecutionPriceType(orderSide: OrderSideEnum) {
  switch (orderSide) {
    case OrderSideEnum.Buy:
      return 'Ask';
    case OrderSideEnum.Sell:
      return 'Bid';

    default:
      return 'n/a';
  }
}

export const mapOrderType = (
  side: SOROrderSide,
  type: SOROrderType,
  isInPendingOrderTable = false,
  isMobile = false,
): string => {
  if (!type || !side) return INVALID_VALUE;

  let result: string = TextHelpers.getOrderTypeName(SOROrderType[type], false, isMobile);

  switch (side) {
    case SOROrderSide.Buy:
      result += ` ${lang.commonOrderSideButtonBuy()}`;
      break;
    case SOROrderSide.Sell:
      result += ` ${lang.commonOrderSideButtonSell()}`;
      break;
    case SOROrderSide.SellShort:
      result += isInPendingOrderTable ? ` ${lang.commonShortSell()}` : ` ${lang.commonOrderSideButtonSell()}`;
      break;
    default:
      break;
  }

  return result;
};

export const convertOrderSideToText = (side: OrderSideEnum|NullableString): string => {
  switch (side) {
    case OrderSideEnum.Buy: {
      return lang.commonOrderSideButtonBuy();
    }
    case OrderSideEnum.Sell: {
      return lang.commonOrderSideButtonSell();
    }
    case OrderSideEnum.SellShort: {
      return lang.commonShortSell3();
    }
    default: return '';
  }
};

export const convertOrderTextToSide = (text: string): string => {
  switch (text) {
    case `${lang.commonOrderSideButtonBuy()}`: {
      return OrderSideEnum.Buy;
    }
    case `${lang.commonOrderSideButtonSell()}`: {
      return OrderSideEnum.Sell;
    }
    case lang.commonShortSell2(): {
      return OrderSideEnum.SellShort;
    }
    default: return '';
  }
};
